import { type DragEvent, useCallback, useRef, useState } from 'react';

interface VideoDropOptions {
  /** Ignore drops entirely, e.g. while an upload is already in flight. */
  disabled?: boolean;
  /** The drop event is passed so callers can place content at the drop point. */
  onFile: (file: File, event: DragEvent) => void;
  /** Stop video drags from bubbling. Portal children need this: React routes their
   *  events through the composer tree, so a popover drop would also hit the composer. */
  stopPropagation?: boolean;
}

// Only video file drags are handled; anything else (editor content, images) passes
// through to the editor untouched. Files are hidden until drop, but items expose the
// MIME type during the drag. Unsupported video formats still reach the upload's validation.
const isVideo = (type: string) => type.startsWith('video/');
const hasVideo = (event: DragEvent) =>
  Array.from(event.dataTransfer?.items ?? []).some((item) => item.kind === 'file' && isVideo(item.type));

/**
 * Native video drop for a composer surface. Tracks nested enter/leave with a depth
 * counter so moving across child elements does not flicker the active state, and
 * hands back the first dropped video file.
 */
export const useVideoDrop = ({ disabled, onFile, stopPropagation }: VideoDropOptions) => {
  const [isDragActive, setIsDragActive] = useState(false);
  const depth = useRef(0);

  const reset = useCallback(() => {
    depth.current = 0;
    setIsDragActive(false);
  }, []);

  const onDragEnter = useCallback(
    (event: DragEvent) => {
      if (!hasVideo(event)) return;
      if (stopPropagation) event.stopPropagation();
      event.preventDefault();
      if (disabled) return;
      depth.current += 1;
      setIsDragActive(true);
    },
    [disabled, stopPropagation],
  );

  const onDragLeave = useCallback(
    (event: DragEvent) => {
      if (!hasVideo(event)) return;
      if (stopPropagation) event.stopPropagation();
      if (disabled) return;
      depth.current -= 1;
      if (depth.current <= 0) reset();
    },
    [disabled, stopPropagation, reset],
  );

  const onDragOver = useCallback(
    (event: DragEvent) => {
      if (!hasVideo(event)) return;
      if (stopPropagation) event.stopPropagation();
      // Always cancel, even while disabled, otherwise the browser navigates to the
      // dropped file and the in-flight upload and draft are lost.
      event.preventDefault();
      if (disabled && event.dataTransfer) event.dataTransfer.dropEffect = 'none';
    },
    [disabled, stopPropagation],
  );

  const onDrop = useCallback(
    (event: DragEvent) => {
      if (!hasVideo(event)) return;
      if (stopPropagation) event.stopPropagation();
      event.preventDefault();
      if (disabled) return;
      reset();
      const file = Array.from(event.dataTransfer?.files ?? []).find((dropped) => isVideo(dropped.type));
      if (file) onFile(file, event);
    },
    [disabled, stopPropagation, onFile, reset],
  );

  return { isDragActive, dropHandlers: { onDragEnter, onDragLeave, onDragOver, onDrop } };
};

export type VideoDropHandlers = ReturnType<typeof useVideoDrop>['dropHandlers'];
